import React from "react";

interface PaginationProps {
  currentPage: number;
  totalPages: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  onPageSizeChange?: (size: number) => void;
  isLoading?: boolean;
}

const Pagination: React.FC<PaginationProps> = ({
  currentPage,
  totalPages,
  onPageChange,
  onPageSizeChange,
  pageSize,
  isLoading = false,
}) => {
  const handlePrevPage = () => {
    if (currentPage > 1 && !isLoading) {
      onPageChange(currentPage - 1);
    }
  };

  const handleNextPage = () => {
    if (currentPage < totalPages && !isLoading) {
      onPageChange(currentPage + 1);
    }
  };

  const renderPageNumbers = () => {
    if (isLoading) {
      return <span className="text-xs text-gray-500 dark:text-gray-400 px-2">Loading...</span>;
    }

    const pages: React.ReactNode[] = [];
    const pageRange = 2;

    if (currentPage > pageRange + 1) {
      pages.push(
        <button
          key="first"
          onClick={() => onPageChange(1)}
          disabled={isLoading}
          className="cursor-pointer text-[12px] px-2 text-gray-700 dark:text-gray-300"
        >
          1
        </button>
      );

      if (currentPage > pageRange + 2) {
        pages.push(
          <span key="start-ellipsis" className="text-[12px] px-1 text-gray-500 dark:text-gray-400">
            ...
          </span>
        );
      }
    }

    for (
      let i = Math.max(1, currentPage - pageRange);
      i <= Math.min(totalPages, currentPage + pageRange);
      i++
    ) {
      pages.push(
        <button
          key={i}
          onClick={() => onPageChange(i)}
          disabled={isLoading}
          className={`cursor-pointer text-[12px] px-3 py-1 rounded ${currentPage === i
              ? "bg-[#F2F4F7] dark:bg-gray-700 text-[#475467] dark:text-gray-200 font-medium"
              : "text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800"
            } ${isLoading ? "opacity-50 cursor-not-allowed" : ""}`}
        >
          {i}
        </button>
      );
    }

    if (currentPage < totalPages - pageRange) {
      if (currentPage < totalPages - pageRange - 1) {
        pages.push(
          <span key="end-ellipsis" className="text-[12px] px-1 text-gray-500 dark:text-gray-400">
            ...
          </span>
        );
      }

      pages.push(
        <button
          key="last"
          onClick={() => onPageChange(totalPages)}
          disabled={isLoading}
          className="cursor-pointer px-2 text-[12px] text-gray-700 dark:text-gray-300"
        >
          {totalPages}
        </button>
      );
    }

    return pages;
  };

  return (
    <div className="flex flex-col sm:flex-row justify-between items-center py-3 px-6 gap-4">

      {/* Left */}
      <div className="flex items-center gap-2">
        <button
          className="border border-gray-200 dark:border-gray-700 bg-[#fff] dark:bg-gray-800 text-gray-700 dark:text-gray-200 text-[12px] justify-center items-center flex gap-3 px-3 h-8 font-medium rounded-lg disabled:opacity-40 disabled:cursor-not-allowed"
          onClick={handlePrevPage}
          disabled={currentPage === 1 || isLoading}
        >
          Previous
        </button>

        <div className="flex gap-1 items-center">{renderPageNumbers()}</div>

        <button
          className="border border-gray-200 dark:border-gray-700 text-[12px] bg-[#fff] dark:bg-gray-800 text-gray-700 dark:text-gray-200 justify-center items-center flex gap-3 px-3 h-8 font-medium rounded-lg disabled:opacity-40 disabled:cursor-not-allowed"
          onClick={handleNextPage}
          disabled={currentPage === totalPages || isLoading}
        >
          Next
        </button>
      </div>

      {/* Left */}
      <div className="flex items-center gap-4 flex-wrap">
        <div className="flex items-center gap-2">
          <span className="text-xs whitespace-nowrap text-gray-600 dark:text-gray-400">Items per page</span>
          <select
            value={pageSize}
            onChange={(e) => onPageSizeChange?.(Number(e.target.value))}
            disabled={isLoading}
            className="border border-gray-300 dark:border-gray-700 rounded px-3 py-1 text-xs bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 focus:outline-none focus:ring-1 focus:ring-gray-500"
          >
            {[10, 20, 30, 40, 50, 60, 70, 80, 100].map((size) => (
              <option key={size} value={size}>
                {size}
              </option>
            ))}
          </select>
        </div>

        <span className="text-xs whitespace-nowrap font-medium text-gray-600 dark:text-gray-400">
          Page {currentPage} of {totalPages}
        </span>
      </div>
    </div>
  );
};

export default Pagination;